import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import ErrorBoundary from "@/components/app/ErrorBoundary";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import GoogleCallback from "@/pages/GoogleCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import Home from "@/pages/Home";
import People from "@/pages/People";
import Library from "@/pages/Library";
import WeekView from "@/pages/WeekView";
import Records from "@/pages/Records";
import History from "@/pages/History";
import Billing, { BillingSuccess, AdminPackages, AthleteBilling } from "@/pages/Billing";
import PackEdit from "@/pages/PackEdit";
import Progress from "@/pages/Progress";
import PersonalData from "@/pages/PersonalData";
import Revenue from "@/pages/Revenue";
import LoginPhotos from "@/pages/LoginPhotos";
import { Toaster } from "@/components/ui/sonner";
import { installOfflineLayer } from "@/lib/offline";

installOfflineLayer();

function AthleteSelf({ Component }) {
  const { user } = useAuth();
  return <Component athleteIdOverride={user.id} />;
}

// Emergent Google sign-in returns the user to `${origin}/#session_id=…`
// (we tell it to redirect to the app root, no custom callback path, per
// the Emergent playbook — multi-segment callback paths were producing a
// Google 400 "malformed request" at the OAuth step). Detect that hash
// synchronously during render BEFORE any ProtectedRoute fires so the
// one-time session_id isn't lost to a login redirect.
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <GoogleCallback />;
  }
  return <AppRouteTable />;
}

export default function App() {
  return (
    <div className="App min-h-screen bg-bone">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

function AppRouteTable() {
  return (
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword mode="forgot" />} />
            <Route path="/reset-password" element={<ForgotPassword mode="reset" />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout><Home /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><People /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><Library /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/week"
              element={
                <ProtectedRoute>
                  <Layout><WeekView /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/records"
              element={
                <ProtectedRoute>
                  <Layout><Records /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/history"
              element={
                <ProtectedRoute>
                  <Layout><History /></Layout>
                </ProtectedRoute>
              }
            />

            {/* Athlete + coach + admin shortcuts — same routes work for "self" */}
            <Route
              path="/week"
              element={
                <ProtectedRoute>
                  <Layout><AthleteSelf Component={WeekView} /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/records"
              element={
                <ProtectedRoute>
                  <Layout><AthleteSelf Component={Records} /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <Layout><AthleteSelf Component={History} /></Layout>
                </ProtectedRoute>
              }
            />

            {/* Billing — class packs + Stripe checkout */}
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Layout><Billing /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute>
                  <Layout><BillingSuccess /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/packages"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><AdminPackages /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/billing"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><AthleteBilling /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/packs/:packId"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><PackEdit /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/revenue"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><Revenue /></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/login-photos"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Layout><LoginPhotos /></Layout>
                </ProtectedRoute>
              }
            />

            {/* Body progress photos */}
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <Layout><Progress /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId/progress"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><Progress /></Layout>
                </ProtectedRoute>
              }
            />

            {/* Personal data — sizing + anything self-service that doesn't
                belong on the Progress (body photos) page. */}
            <Route
              path="/personal"
              element={
                <ProtectedRoute>
                  <Layout><PersonalData /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId/personal"
              element={
                <ProtectedRoute roles={["admin", "coach"]}>
                  <Layout><PersonalData /></Layout>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}
